import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PaymentResultShell from './PaymentResultShell';
import { useTracking } from '../../hooks/useTracking';

const REDIRECT_SECONDS = 12;

/**
 * Parámetros habituales al volver de MercadoPago (back_urls / return).
 * Ajustá según lo que envíes en external_reference / preference.
 */
function useMercadoPagoParams() {
  const [searchParams] = useSearchParams();
  return {
    collectionStatus: searchParams.get('collection_status'),
    paymentId: searchParams.get('payment_id'),
    preferenceId: searchParams.get('preference_id'),
    externalReference: searchParams.get('external_reference'),
    status: searchParams.get('status'),
  };
}

const Success = ({ clearCart }) => {
  const { trackPurchase } = useTracking();
  const navigate = useNavigate();
  const mp = useMercadoPagoParams();
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);
  const [autoRedirect, setAutoRedirect] = useState(true);

  const orderId =
    mp.externalReference || mp.preferenceId || mp.paymentId || `order_${Date.now()}`;

  useEffect(() => {
    document.title = 'Pago exitoso · Privistore';
    return () => {
      document.title = 'Privistore';
    };
  }, []);

  // Solo cuando el pago está acreditado (no pendiente/rechazado) vaciamos carrito y trackeamos
  const paymentApproved =
    mp.collectionStatus === 'approved' || mp.status === 'approved';

  useEffect(() => {
    if (typeof clearCart === 'function' && paymentApproved) clearCart();
  }, [clearCart, paymentApproved]);

  useEffect(() => {
    if (!paymentApproved) return;
    const items = [{ id: orderId, name: 'Compra', price: 0, quantity: 1 }];
    trackPurchase({
      id: String(orderId),
      total: 0,
      items,
    });
  }, [trackPurchase, orderId, paymentApproved]);

  const goHome = useCallback(() => navigate('/', { replace: true }), [navigate]);

  useEffect(() => {
    if (!autoRedirect) return undefined;
    if (secondsLeft <= 0) {
      goHome();
      return undefined;
    }
    const t = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [autoRedirect, secondsLeft, goHome]);

  const hasMeta =
    mp.externalReference || mp.paymentId || mp.preferenceId || mp.collectionStatus;

  const isPending =
    hasMeta &&
    !paymentApproved &&
    (mp.collectionStatus === 'pending' ||
      mp.collectionStatus === 'in_process' ||
      mp.status === 'pending' ||
      mp.status === 'in_process');

  return (
    <PaymentResultShell
      variant="success"
      title={isPending ? 'Pago en proceso' : 'Pago exitoso'}
      subtitle={
        isPending
          ? 'Tu pago está siendo acreditado. Mantuvimos tu carrito por si necesitás volver a intentar; cuando el pago se acredite te avisaremos y podés cerrar esta pestaña.'
          : 'Gracias por tu compra. Recibimos el pago correctamente; te contactaremos si hace falta algún dato.'
      }
      primaryLabel="Ir al inicio"
      primaryTo="/"
      secondaryLabel="Ver productos"
      secondaryTo="/productos"
    >
      {hasMeta && (
        <dl className="mp-result__meta">
          {mp.externalReference && (
            <>
              <dt>Referencia</dt>
              <dd>{mp.externalReference}</dd>
            </>
          )}
          {mp.paymentId && (
            <>
              <dt>ID de pago</dt>
              <dd>{mp.paymentId}</dd>
            </>
          )}
          {mp.preferenceId && (
            <>
              <dt>Preferencia</dt>
              <dd>{mp.preferenceId}</dd>
            </>
          )}
          {mp.collectionStatus && (
            <>
              <dt>Estado</dt>
              <dd>{mp.collectionStatus}</dd>
            </>
          )}
        </dl>
      )}
      <p className="mp-result__countdown">
        {autoRedirect ? (
          <>
            Te redirigimos al inicio en <strong>{secondsLeft}</strong> s.
            <button type="button" onClick={() => setAutoRedirect(false)}>
              Cancelar
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => {
              setSecondsLeft(REDIRECT_SECONDS);
              setAutoRedirect(true);
            }}
          >
            Volver a activar redirección automática
          </button>
        )}
      </p>
    </PaymentResultShell>
  );
};

export default Success;
